import { Button } from '@/components/common/Button';
import { Checkbox } from '@/components/common/Checkbox';
import Combobox from '@/components/common/Combobox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/common/DropdownMenu';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/common/Select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/common/Tabs';
import { ActionDropdownItem } from '@/components/general/ActionDropdownItem';
import Table from '@/components/table/Table';
import { DATE_YMD_HMS_12H_FORMAT, DEFAULT_SELECT_PAGE_SIZES, OWNING_SHOP_STATUS, ROUTES, SHOP_RATING, SHOP_STATUS, USER_STATUS } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { LANGUAGES_SUPPORTED, SHOP_STATUS_TYPE, USER_ROLE_TYPE, USER_STATUS_TYPE, UserStatusType } from '@/types/enums';
import { User } from '@/types/user';
import { formatDateTime } from '@/utils/datetime';
import { toastify } from '@/utils/toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import classNames from 'classnames';
import { BanIcon, CheckIcon, CircleCheckIcon, EllipsisIcon, PlusIcon, SearchIcon, SquarePenIcon, Trash2Icon } from 'lucide-react';
import { flowResult, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import AddUserModal from './components/AddUserModal';
import { useNavigate } from 'react-router-dom';
import { formatNumber, scrollToTop } from '@/utils/utils';

// validate schema
const validateSchema = yup.object().shape({
    fullName: yup
        .string(),
    phoneNumber: yup
        .string(),
    provinceName: yup
        .string(),
    provinceCode: yup
        .string(),
    owningShop: yup.array().of(yup.boolean()),
    status: yup.string()
});

type FormData = yup.InferType<typeof validateSchema>;

export default observer(function UserManagementPage() {
    // hooks
    const { t, i18n: { language } } = useTranslation();

    // store
    const {
        userStore: { users, getUsers, totalByRole, paging, totalRecord,
            setObservable, keepActivedTab, keepSearchParams, defaultPaging },
        generalStore: { administrativeUnit },
        modalStore: { showModal }
    } = useStore();

    const initialValues = {
        fullName: '',
        phoneNumber: '',
        provinceName: 'all',
        provinceCode: '',
        owningShop: [],
        status: 'all'
    };

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: initialValues
    });

    // State
    const [activeTab, setActiveTab] = useState<string>(keepActivedTab ? keepActivedTab : USER_ROLE_TYPE.Enduser.toString());
    const [searchState, setSearchState] = useState<Object>(keepSearchParams ?? '');

    // lifecycle
    useEffect(() => {
        if (keepSearchParams) {
            form.reset(keepSearchParams);
            onFetchUser(keepSearchParams, paging);
        }
    }, []);

    useEffect(() => {
        onFetchUser(searchState);
    }, [activeTab]);

    // function
    const onSearch = (searchParams: FormData) => {
        setSearchState(searchParams);
        onFetchUser(searchParams, defaultPaging);
    };

    const onFetchUser = (searchParams?: any, paging?: TableState) => {
        if (paging) {
            setObservable('paging', paging ?? null);
        }

        setObservable('keepActivedTab', activeTab);
        setObservable('keepSearchParams', toJS(searchParams));
        getUsers(convertFormdataToPayload(searchParams), paging);
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging = stateUpdater instanceof Function ? stateUpdater(paging) : stateUpdater;
        onFetchUser(searchState, newPaging);
        scrollToTop();
    };

    const handleTabChange = (tabValue: string) => {
        setActiveTab(tabValue);
    };

    const handleClickAddUser = () => {
        showModal({
            size: '4xl',
            title: t('words_title.add_user'),
            content: <AddUserModal onFetch={onFetchUser} />,
            footerClassName: 'hidden'
        });
    };

    const convertFormdataToPayload = (data: FormData) => {
        const { fullName, phoneNumber, provinceCode, owningShop, status } = data ?? {};
        const [yes, no] = owningShop ?? [];
        const owningShopConvert = yes && no ?
            [true, false] :
            yes ?
                [true] :
                no ?
                    [false] :
                    [];
        return {
            fullName,
            phoneNumber,
            provinceCode,
            owningShop: owningShopConvert,
            status: (status && Object.keys(USER_STATUS).find(item => item === status)),
            role: activeTab
        };
    };

    // columns
    const columnsEnduser: ColumnDef<User>[] = [
        {
            header: t('words_title.index'),
            size: 10,
            enableSorting: false,
            cell: ({ row: { index } }) => (
                <div>{paging.pagination.pageIndex * paging.pagination.pageSize + index + 1}</div>
            )
        },
        {
            accessorKey: 'fullName',
            header: t('words_title.full_name'),
            enableSorting: false,
            size: 100,
            cell: ({ row: { original } }) => (
                <div>{original?.fullName ?? ''}</div>
            )
        },
        {
            accessorKey: 'phoneNumber',
            header: t('words_title.phone_number'),
            enableSorting: false,
            size: 50,
            cell: ({ row: { original } }) => (
                <div>{original?.phoneNumber ?? ''}</div>
            )
        },
        {
            accessorKey: 'email',
            header: t('words_title.email'),
            enableSorting: false,
            size: 100,
            cell: ({ row: { original } }) => (
                <div>{original?.email ?? ''}</div>
            )
        },
        {
            accessorKey: 'city',
            header: t('words_title.city'),
            enableSorting: false,
            size: 80,
            cell: ({ row: { original } }) => {
                const province = toJS(administrativeUnit).provinces.find(item => (
                    item?.code?.toString() === original.province?.code
                ));

                return (
                    <div>
                        {province ?
                            (
                                language === LANGUAGES_SUPPORTED.English ? province.nameEn : province.name
                            ) :
                            ''}
                    </div>
                );
            }
        },
        {
            accessorKey: 'owningShop',
            header: t('words_title.owning_shop'),
            enableSorting: false,
            size: 50,
            cell: ({ row: { original } }) => (
                <div className='flex items-center'>
                    <div>{original.owningShop ? <CheckIcon className='h-4.5 w-4.5 text-primary' /> : ''}</div>
                </div>
            )
        },
        {
            accessorKey: 'createdAt',
            header: t('words_title.created_date'),
            enableSorting: false,
            size: 100,
            cell: ({ row: { original } }) => (
                <div>{original?.createdAt ? formatDateTime(original.createdAt, DATE_YMD_HMS_12H_FORMAT) : ''}</div>
            )
        },
        {
            accessorKey: 'status',
            header: t('words_title.status'),
            enableSorting: false,
            size: 80,
            cell: ({ row: { original } }) => (
                <ShopStatusColumn type={original.status} />
            )
        },
        {
            id: 'actions',
            size: 10,
            cell: ({ row: { original } }) => (
                <div className='flex justify-center items-center'>
                    <ActionMenuDropdown
                        status={original.status}
                        code={original.code}
                        onFetch={onFetchUser}
                    />
                </div>
            )
        }
    ];

    const columnsAdmin: ColumnDef<User>[] = [
        {
            header: t('words_title.index'),
            size: 10,
            enableSorting: false,
            cell: ({ row: { index } }) => (
                <div>{paging.pagination.pageIndex * paging.pagination.pageSize + index + 1}</div>
            )
        },
        {
            accessorKey: 'fullName',
            header: t('words_title.full_name'),
            enableSorting: false,
            size: 100,
            cell: ({ row: { original } }) => (
                <div>{original?.fullName ?? ''}</div>
            )
        },
        {
            accessorKey: 'phoneNumber',
            header: t('words_title.phone_number'),
            enableSorting: false,
            size: 50,
            cell: ({ row: { original } }) => (
                <div>{original?.phoneNumber ?? ''}</div>
            )
        },
        {
            accessorKey: 'email',
            header: t('words_title.email'),
            enableSorting: false,
            size: 100,
            cell: ({ row: { original } }) => (
                <div>{original?.email ?? ''}</div>
            )
        },
        {
            accessorKey: 'city',
            header: t('words_title.city'),
            enableSorting: false,
            size: 80,
            cell: ({ row: { original } }) => {
                const province = toJS(administrativeUnit).provinces.find(item => (
                    item?.code?.toString() === original.province?.code
                ));

                return (
                    <div>
                        {province ?
                            (
                                language === LANGUAGES_SUPPORTED.English ? province.nameEn : province.name
                            ) :
                            ''}
                    </div>
                );
            }
        },
        {
            accessorKey: 'createdAt',
            header: t('words_title.created_date'),
            enableSorting: false,
            size: 100,
            cell: ({ row: { original } }) => (
                <div>{original?.createdAt ? formatDateTime(original.createdAt, DATE_YMD_HMS_12H_FORMAT) : ''}</div>
            )
        },
        {
            accessorKey: 'status',
            header: t('words_title.status'),
            enableSorting: false,
            size: 80,
            cell: ({ row: { original } }) => (
                <ShopStatusColumn type={original.status} />
            )
        },
        {
            id: 'actions',
            size: 10,
            cell: ({ row: { original } }) => (
                <div className='flex justify-center items-center'>
                    <ActionMenuDropdown
                        status={original.status}
                        code={original.code}
                        onFetch={onFetchUser}
                    />
                </div>
            )
        }
    ];

    return (
        <div>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSearch)}>
                    <div className='bg-white rounded p-4'>
                        <div className='grid grid-cols-2 gap-x-20 gap-y-4'>
                            <FormField
                                control={form.control}
                                name='fullName'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/5 label-1'>{t('words_title.full_name')}</Label>
                                            <FormControl className='w-4/5'>
                                                <Input type='text' {...field} />
                                            </FormControl>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name='phoneNumber'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/5 label-1'>{t('words_title.phone_number')}</Label>
                                            <FormControl className='w-4/5'>
                                                <Input
                                                    type='number'
                                                    isTypingOnlyNumber={true}
                                                    {...field}
                                                />
                                            </FormControl>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name='provinceName'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/5 label-1'>{t('words_title.city')}</Label>
                                            <FormControl className='w-4/5'>
                                                <Combobox
                                                    // key={language}
                                                    placeholder={t('placeholder.choose')}
                                                    value={field.value ?? ''}
                                                    onChange={(item) => {
                                                        field.onChange(item?.value);
                                                        form.setValue('provinceCode', item?.code);
                                                    }}
                                                    items={
                                                        (Array.isArray(administrativeUnit?.provinces) ?
                                                            [
                                                                { label: t('words_title.all'), value: 'all' },
                                                                ...administrativeUnit.provinces.map(item => ({
                                                                    label: language === LANGUAGES_SUPPORTED.English ? item.fullNameEn : item.fullName,
                                                                    value: item.name,
                                                                    code: item.code
                                                                }))] :
                                                            []
                                                        )
                                                    }
                                                    side='top'
                                                />
                                            </FormControl>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className='flex items-center'>
                                <Label className='w-1/5 label-1'>{t('words_title.owning_shop')}</Label>
                                <div className='w-4/5 flex items-center gap-8'>
                                    {
                                        Object.keys(OWNING_SHOP_STATUS).map((item, index) => (
                                            <FormField
                                                key={index}
                                                control={form.control}
                                                name={`owningShop.${index}`}
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <div className='flex gap-2 items-center'>
                                                            <FormControl>
                                                                <Checkbox
                                                                    checked={field.value}
                                                                    onCheckedChange={field.onChange}
                                                                />
                                                            </FormControl>
                                                            <Label>{t(`words_title.${OWNING_SHOP_STATUS[item].key}`)}</Label>
                                                        </div>
                                                    </FormItem>
                                                )}
                                            />
                                        ))
                                    }
                                </div>
                            </div>

                            <FormField
                                control={form.control}
                                name='status'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/5 label-1'>{t('words_title.status')}</Label>
                                            <Select
                                                key={field.value}
                                                onValueChange={field.onChange}
                                                {...field}
                                                className='w-4/5'
                                            >
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder={t('placeholder.choose')} />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem
                                                        className='hover:cursor-pointer'
                                                        value='all'
                                                    >
                                                        <span>{t('words_title.all')}</span>
                                                    </SelectItem>
                                                    {Object.keys(USER_STATUS).map(item => (
                                                        <SelectItem
                                                            className='hover:cursor-pointer'
                                                            key={SHOP_RATING[item].key}
                                                            value={item}
                                                        >
                                                            <span>{t(`words_title.${USER_STATUS[item].key}`)}</span>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                        </div>
                        <div className='flex justify-center mt-8'>
                            <Button variant='outline' className='min-w-48 flex items-center justify-center gap-2 group'>
                                <SearchIcon className='w-5 h-5 text-active group-hover:text-white' />
                                <span>{t('button.search')}</span>
                            </Button>
                        </div>
                    </div>
                </form>
            </Form>

            <Tabs value={activeTab} onValueChange={handleTabChange} className='w-full h-full flex flex-col'>
                <div className='bg-white mt-4 rounded p-4'>
                    <Button variant='default' className='text-white flex items-center justify-center gap-2 min-w-28 ml-auto' onClick={handleClickAddUser}>
                        <PlusIcon className='w-7 h-7 text-white' />
                        <span>{t('button.add_user')}</span>
                    </Button>
                    <TabsList variant='shop' data-orientation='horizontal' className='flex w-full mt-6'>
                        <TabsTrigger
                            variant='shop'
                            className='md:text-[0.875rem] text-3 w-full'
                            value={USER_ROLE_TYPE.Enduser.toString()}
                        >
                            {`${t('words_title.all_user')} (${formatNumber(Number(totalByRole[USER_ROLE_TYPE.Enduser] ?? '0'))})`}
                        </TabsTrigger>

                        <TabsTrigger
                            variant='shop'
                            className='md:text-[0.875rem] text-3 w-full'
                            value={USER_ROLE_TYPE.Admin.toString()}
                        >
                            {`${t('words_title.administrator')} (${formatNumber(Number(totalByRole[USER_ROLE_TYPE.Admin] ?? '0'))})`}
                        </TabsTrigger>
                    </TabsList>
                </div>

                <div className='bg-white rounded p-4'>
                    <TabsContent value={USER_ROLE_TYPE.Enduser.toString()}>
                        <Table
                            columns={columnsEnduser}
                            data={users}
                            rowCount={totalRecord}
                            onStateChange={onStateChange}
                            state={paging}
                            selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                            getRowId={row => row.id?.toString()}
                        />
                    </TabsContent>
                    <TabsContent value={USER_ROLE_TYPE.Admin.toString()}>
                        <Table
                            columns={columnsAdmin}
                            data={users}
                            rowCount={totalRecord}
                            onStateChange={onStateChange}
                            state={paging}
                            selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                            getRowId={row => row.id?.toString()}
                        />
                    </TabsContent>
                </div>
            </Tabs>
        </div>
    );
});

interface ShopStatusColumnProps {
    type: UserStatusType
}

const ShopStatusColumn = ({ type }: ShopStatusColumnProps) => {
    // hooks
    const { t } = useTranslation();

    return (
        <div className='flex items-center justify-start gap-2'>
            <div className={classNames('w-2 h-2 rounded-full flex-none', SHOP_STATUS[type].color)}></div>
            <div>{t(`words_title.${SHOP_STATUS[type].key}`)}</div>
        </div>
    );
};

type ActionMenuDropdownProps = {
    status: number,
    code: string,
    onFetch: Function
};

const ActionMenuDropdown = ({ status, code, onFetch }: ActionMenuDropdownProps) => {
    // hooks
    const { t } = useTranslation();
    const navigate = useNavigate();

    // store
    const {
        userStore: { updateStatus, deleteUser, keepSearchParams, paging },
        modalStore: { showAlertModal, modalWaringAlertOptions }
    } = useStore();

    // function
    const handleClickInactive = () => {
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            content: t('messages.confirm_inactive_user'),
            onSave: async () => {
                const res = await flowResult(updateStatus(code, { status: USER_STATUS_TYPE.Inactive }));
                if (res && res.ok) {
                    onFetch(keepSearchParams, paging);
                    toastify('success', t('messages.update_success'));
                }
            }
        }));
    };

    const handleClickActive = () => {
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            content: t('messages.confirm_active_user'),
            onSave: async () => {
                const res = await flowResult(updateStatus(code, { status: USER_STATUS_TYPE.Active }));
                if (res && res.ok) {
                    onFetch(keepSearchParams, paging);
                    toastify('success', t('messages.update_success'));
                }
            }
        }));
    };

    const handleClickDelete = () => {
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            content: t('messages.confirm_delete_user'),
            onSave: async () => {
                const res = await flowResult(deleteUser(code));
                if (res && res.ok) {
                    onFetch(keepSearchParams, paging);
                    toastify('success', t('messages.delete_success'));
                }
            }
        }));
    };

    const handleClickEdit = () => {
        navigate(`${ROUTES.userManagement.href}/${code}`);
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <EllipsisIcon className='h-6 w-6 text-active hover:cursor-pointer' />
            </DropdownMenuTrigger>

            <DropdownMenuContent className='w-48' align='end' forceMount>

                {
                    status === SHOP_STATUS_TYPE.Inactive && (
                        <>
                            <DropdownMenuItem className='hover:cursor-pointer' asChild>
                                <ActionDropdownItem icon={CircleCheckIcon} text={t('words_title.active_action')} onClick={handleClickActive} />
                            </DropdownMenuItem>
                        </>
                    )
                }

                {
                    status === SHOP_STATUS_TYPE.Active && (
                        <>
                            <DropdownMenuItem className='hover:cursor-pointer' asChild>
                                <ActionDropdownItem icon={BanIcon} text={t('words_title.inactive_action')} onClick={handleClickInactive} />
                            </DropdownMenuItem>
                        </>
                    )
                }
                <DropdownMenuSeparator />
                <DropdownMenuItem className='hover:cursor-pointer' asChild>
                    <ActionDropdownItem icon={SquarePenIcon} text={t('words_title.edit_action')} onClick={handleClickEdit} />
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className='hover:cursor-pointer' asChild>
                    <ActionDropdownItem icon={Trash2Icon} text={t('words_title.delete_action')} onClick={handleClickDelete} />
                </DropdownMenuItem>

            </DropdownMenuContent>
        </DropdownMenu>
    );
};
